import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import './FeeEstimator.css'
import hederaFeeData, { calculateTotalFee } from './data/hederaFees'
import { ServiceIcons } from './components/icons/ServiceIcons'
import { InfoIcon } from './components/icons/InfoIcon'

function App() {
  const [selectedService, setSelectedService] = useState('')
  const [selectedApi, setSelectedApi] = useState('')
  const [selections, setSelections] = useState([])
  const [hoveredApi, setHoveredApi] = useState(null)
  const [exchangeRate, setExchangeRate] = useState(hederaFeeData.exchangeRate.hbarToUSD) // Default exchange rate
  const [isTableExpanded, setIsTableExpanded] = useState(true)
  const [isColumn1Expanded, setIsColumn1Expanded] = useState(true)
  const [isColumn2Expanded, setIsColumn2Expanded] = useState(false)
  const [currentRowIndex, setCurrentRowIndex] = useState(0)
  const selectionIdRef = useRef(0)
  const tableRowsRef = useRef(null)
  const estimateSummaryRef = useRef(null)

  // Fetch exchange rate on mount
  useEffect(() => {
    const endpoint = import.meta.env.VITE_PRICING_API_ENDPOINT;
    if (!endpoint) {
      //console.log('No pricing API endpoint configured, using default rate');
      return;
    }

    axios.get(endpoint)
      .then(response => {
        const hbars = response.data[0]["CurrentRate"]["hbarEquiv"];
        const cents = response.data[0]["CurrentRate"]["centEquiv"];
        const rateOfHbarInCents = cents / hbars;
        //console.log('Exchange rate in cents = ', rateOfHbarInCents);
        setExchangeRate(rateOfHbarInCents / 100); // Convert cents to dollars
      })
      .catch(error => {
        //console.log('Exchange API error = ', error);
        //console.log('Setting exchange rate to default: $0.12 per HBAR');
        setExchangeRate(0.12);
      });
  }, []);

  // Calculate actual content height for smooth accordion animation
  useEffect(() => {
    if (tableRowsRef.current) {
      const height = tableRowsRef.current.scrollHeight;
      tableRowsRef.current.style.setProperty('--content-height', `${height}px`);
    }
  }, [selections, isTableExpanded]);

  // Track scroll position to update current row index
  useEffect(() => {
    const container = tableRowsRef.current;
    if (!container) return;

    const handleScroll = () => {
      const rows = container.querySelectorAll('.table-row');
      if (rows.length === 0) return;

      const containerLeft = container.scrollLeft;
      let closestIndex = 0;
      let minDistance = Infinity;

      rows.forEach((row, index) => {
        const rowLeft = row.offsetLeft - container.offsetLeft;
        const distance = Math.abs(containerLeft - rowLeft);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      setCurrentRowIndex(closestIndex);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selections]);

  const currentService = hederaFeeData.services.find(s => s.id === selectedService)
  const currentApi = currentService?.apis.find(a => a.id === selectedApi)
  const isApiAlreadyAdded = currentApi ? selections.some(s => s.serviceId === selectedService && s.apiId === selectedApi) : false

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId)
    setSelectedApi('')
    setIsColumn1Expanded(false)
    setIsColumn2Expanded(true)
  }

  const handleApiSelect = (apiId) => {
    setSelectedApi(apiId)
    setIsColumn2Expanded(false)
  }

  const handleAddToEstimate = () => {
    if (!selectedService || !selectedApi) return

    selectionIdRef.current += 1

    const newSelection = {
      id: `selection-${selectionIdRef.current}`,
      serviceId: selectedService,
      serviceName: currentService.name,
      apiId: selectedApi,
      apiName: currentApi.name,
      quantity: 1,
      estimateUSD: currentApi.estimateUSD,
      estimateHBAR: currentApi.estimateUSD / exchangeRate,
      totalUSD: currentApi.estimateUSD,
      totalHBAR: currentApi.estimateUSD / exchangeRate
    }

    setSelections(prev => [...prev, newSelection])
    
    // Reset API selection but keep service selected
    setSelectedApi('')
    // On mobile: show estimate summary, collapse column 2
    setIsColumn2Expanded(false)
    
    // Scroll to estimate summary
    setTimeout(() => {
      if (estimateSummaryRef.current) {
        estimateSummaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleRemoveSelection = (id) => {
    setSelections(selections.filter(s => s.id !== id))
  }

  const handleUpdateQuantity = (id, newQuantity) => {
    const qty = parseInt(newQuantity) || 1
    setSelections(selections.map(selection => {
      if (selection.id === id) {
        return {
          ...selection,
          quantity: qty,
          totalUSD: selection.estimateUSD * qty,
          totalHBAR: selection.estimateHBAR * qty
        }
      }
      return selection
    }))
  }

  const handlePrevRow = () => {
    if (currentRowIndex > 0) {
      const newIndex = currentRowIndex - 1
      setCurrentRowIndex(newIndex)
      scrollToRow(newIndex)
    }
  }

  const handleNextRow = () => {
    if (currentRowIndex < selections.length - 1) {
      const newIndex = currentRowIndex + 1
      setCurrentRowIndex(newIndex)
      scrollToRow(newIndex)
    }
  }

  const scrollToRow = (index) => {
    if (tableRowsRef.current) {
      const rows = tableRowsRef.current.querySelectorAll('.table-row')
      if (rows[index]) {
        const row = rows[index]
        const container = tableRowsRef.current
        const rowLeft = row.offsetLeft - container.offsetLeft

        container.scrollTo({
          left: rowLeft,
          behavior: 'smooth'
        })
      }
    }
  }

  const totals = calculateTotalFee(selections)

  return (
    <div className="hedera-fee-estimator">
      <div className="estimator-container">
        {/* Column 1: Services */}
        <section className="selection-column services-column">
          <div className="column-header" onClick={() => setIsColumn1Expanded(!isColumn1Expanded)}>
            <div>
              <p className="small-caps">step <span className='small-number'>1</span></p>
              <h2 className="h4">Select a service</h2>
            </div>
            <svg className={`accordion-arrow ${isColumn1Expanded ? 'expanded' : ''}`} width="21" height="12" viewBox="0 0 21 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.5312 10.5607L10.0313 1.06067L0.53125 10.5607" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          {selectedService && !isColumn1Expanded && (
            <div className="selected-display">
              <div className="selected-display-item">
              {(() => {
                const IconComponent = ServiceIcons[currentService.iconKey];
                return IconComponent && <IconComponent className="service-icon" />;
              })()}
              <div className="service-name h4">{currentService.name}</div>
              </div>
            </div>
          )}
          <div className={`column-content ${isColumn1Expanded ? 'expanded' : 'collapsed'}`}>
            <div className="service-list">
            {hederaFeeData.services.map(service => {
              const IconComponent = ServiceIcons[service.iconKey];
              return (
                <button
                  key={service.id}
                  className={`service-item ${selectedService === service.id ? 'active' : ''}`}
                  onClick={() => handleServiceSelect(service.id)}
                >
                  {IconComponent && <IconComponent className="service-icon" />}
                  <div className="service-content">
                    <div className="service-name h4">{service.name}</div>                    
                  </div>
                </button>
              );
            })}
          </div>
          </div>
        </section>

        {/* Column 2: APIs */}
        <section className={`selection-column apis-column ${selectedService ? 'visible' : 'hidden-mobile'}`}>
          <div className="column-header" onClick={() => setIsColumn2Expanded(!isColumn2Expanded)}>
            <div>
              <p className="small-caps">step <span className='small-number'>2</span></p>
              <h2 className="h4">Select an operation</h2>
            </div>
            <svg className={`accordion-arrow ${isColumn2Expanded ? 'expanded' : ''}`} width="21" height="12" viewBox="0 0 21 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.5312 10.5607L10.0313 1.06067L0.53125 10.5607" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          {selectedApi && !isColumn2Expanded && (
            <div 
                  key={currentApi.id} 
                  className="api-item-wrapper"
                  onMouseEnter={() => setHoveredApi(currentApi.id)}
                  onMouseLeave={() => setHoveredApi(null)}
                >
            <div className="selected-display">
              <div className="selected-display-item">
                <div className="api-name">{currentApi.name}<InfoIcon className="api-info-icon" /></div>     
              </div>         
            </div>
             {hoveredApi === currentApi.id && (
                    <div className="api-tooltip selected-display-tooltip">
                      {currentApi.description}
                    </div>
                  )}
            </div>
          )}
          {selectedService && (
            <div className={`column-content ${isColumn2Expanded ? 'expanded' : 'collapsed'}`}>
              <div className="api-list">
              {currentService.apis.map(api => (
                <div 
                  key={api.id} 
                  className="api-item-wrapper"
                  onMouseEnter={() => setHoveredApi(api.id)}
                  onMouseLeave={() => setHoveredApi(null)}
                >
                  <button
                    className={`api-item ${selectedApi === api.id ? 'active' : ''}`}
                    onClick={() => handleApiSelect(api.id)}
                  >
                    <div className="api-name">{api.name}<InfoIcon className="api-info-icon" /></div>
                    
                  </button>
                  {hoveredApi === api.id && (
                    <div className="api-tooltip">
                      {api.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
            </div>
          )}
        </section>

        {/* Column 3: Cost & Add Button */}
        <section className={`selection-column cost-column ${currentApi ? 'visible' : 'hidden-mobile'}`}>
          <div className="column-header">
            <div>
              <p className="small-caps">step <span className='small-number'>3</span></p>
              <h2 className="h4">Review and add to estimate</h2>
            </div>
          </div>
          {currentApi && (
            <div className="cost-details">
              {isApiAlreadyAdded ? (
                <div className="duplicate-message">
                  <p>This transaction has already been added. To adjust number of transactions please edit the field below.</p>
                </div>
              ) : (
                <>
                  <div className="price-display">
                    <div className="price-label h4">Transaction estimate:</div>
                    <div className="price-amount">
                      <span className="price-usd h4">{currentApi.estimateUSD.toFixed(4)}<span className="price-currency">USD</span></span>
                      <span className="price-hbar h4">{(currentApi.estimateUSD / exchangeRate).toFixed(6)}<span className="price-currency">ℏ</span></span>
                    </div>
                  </div>

                  {currentApi.note && (
                    <p className="note">{currentApi.note}</p>
                  )}

                  <button 
                    className="btn btn-white btn-add"
                    onClick={handleAddToEstimate}
                  >
                    Add To Estimate Total
                  </button>
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Estimate Summary - Full Width Below */}
      <section ref={estimateSummaryRef} className={`estimate-summary-full ${selections.length > 0 ? 'visible' : 'hidden-mobile'}`}>
        <h2 className="estimate-summary-title h4">Estimate total</h2>
        
        
            <div className="selections-table">
              <div className={`table-header ${selections.length === 0 ? 'empty' : ''}`} onClick={() => setIsTableExpanded(!isTableExpanded)}>                
                <div className="col">
                  <div className="col-api">Network transaction</div>
                </div>
                <div className="col">
                  <div className="col-qty"># of transactions</div>
                  <div className="col-unit-price">Fee per transaction</div>
                </div>
                <div className="col">
                  <div className="col-total-usd">USD</div>
                  <div className="col-total-hbar">ℏ</div>
                  <div className="col-actions">                  
                    <svg  className={`accordion-arrow ${isTableExpanded ? 'expanded' : ''}`} width="21" height="12" viewBox="0 0 21 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.5312 10.5607L10.0313 1.06067L0.53125 10.5607" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                  </div>
                </div>                
              </div>             
              
              <div 
                ref={tableRowsRef}
                className={`table-rows ${isTableExpanded ? 'expanded' : 'collapsed'}`}
              >
                {selections.map(selection => (
                <div key={selection.id} className="table-row">
                  <div className="col">                 
                    <div className="col-api h4">{selection.apiName}</div>
                  </div>
                  <div className="col">   
                    <div className="col-qty">
                      <input 
                        type="number"
                        min="1"
                        value={selection.quantity}
                        onChange={(e) => handleUpdateQuantity(selection.id, e.target.value)}
                        className="qty-input"
                      />
                    </div>
                    <div className="col-unit-price h4">
                      {selection.estimateUSD.toFixed(4)}
                    </div>
                  </div>
                  <div className="col">
                    <div className="col-total-usd h4">
                      {selection.totalUSD.toFixed(4)}
                    </div>
                    <div className="col-total-hbar h4">
                      {selection.totalHBAR.toFixed(6)}
                    </div>
                    <div className="col-actions">
                      <button 
                        className="btn-remove"
                        onClick={() => handleRemoveSelection(selection.id)}
                        aria-label="Remove item"
                      >
                        <svg width="23" height="26" viewBox="0 0 23 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clip-path="url(#clip0_6545_43975)">
                          <path d="M11.5625 8.87085V21.3224" stroke="currentColor" stroke-width="1.75"/>
                          <path d="M7.30078 8.87085V21.3224" stroke="currentColor" stroke-width="1.75"/>
                          <path d="M15.75 8.87085V21.3224" stroke="currentColor" stroke-width="1.75"/>
                          <path d="M2.96875 5.45117H20.0333L20.0103 24.7415H2.99169L2.96875 5.45117Z" stroke="currentColor" stroke-width="1.75"/>
                          <path d="M0 5.45117H23" stroke="currentColor" stroke-width="1.75"/>
                          <rect x="7.41016" y="1" width="8.16129" height="4.45161" stroke="currentColor" stroke-width="1.75"/>
                          </g>
                          <defs>
                          <clipPath id="clip0_6545_43975">
                          <rect width="23" height="26" fill="currentColor"/>
                          </clipPath>
                          </defs>
                        </svg>

                      </button>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            </div>
            <div className='navigation hidden-desktop'>
              <button 
                className="btn btn-white btn-prev" 
                onClick={handlePrevRow}
                disabled={currentRowIndex === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="17" viewBox="0 0 24 17" fill="none"><path d="M23.0144 8.62427L2.62856 8.62426" stroke="currentColor" stroke-width="1.49896"></path><path d="M9.82361 16.4189L2.02902 8.62436L9.82361 0.829773" stroke="currentColor" stroke-width="1.49896"></path></svg>
              </button>
              <button 
                className="btn btn-white btn-next" 
                onClick={handleNextRow}
                disabled={currentRowIndex === selections.length - 1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="17" viewBox="0 0 23 17" fill="none"><path d="M0.0144043 8.62427L20.4002 8.62427" stroke="currentColor" stroke-width="1.49896"></path><path d="M13.2052 0.82959L20.9998 8.62418L13.2052 16.4188" stroke="currentColor" stroke-width="1.49896"></path></svg>
              </button>
            </div>

            <div className="total-section">
              <div className="total-row">
                <span className="total-label h4">Total:</span>
                <div className='total-currencies'>
                  <span className="total-usd h4">{totals.usd === 0 ? 0 : totals.usd.toFixed(4)}<span className="price-space"></span>USD</span>
                  <span className="total-hbar h4">{totals.hbar === 0 ? 0 : totals.hbar.toFixed(6)}<span className="price-space"></span>ℏ</span>
                  <span className="total-actions"></span>
                </div>
              </div>
              {/*
              <button 
                className="btn-clear"
                onClick={() => setSelections([])}
              >
                Clear All
              </button>
              */}
            </div>
          
      </section>

      <div className="estimator-footer">
        <p style={{marginBottom: '0'}}>
          {hederaFeeData.disclaimer.text}
        </p>
        <p>
          {hederaFeeData.disclaimer.link.prependText}           
          <a 
            href={hederaFeeData.disclaimer.link.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={hederaFeeData.disclaimer.link.label}            
          >
            {hederaFeeData.disclaimer.link.text}
          </a>.
        </p>
      </div>
    </div>
  )
}

export default App

